import './dashboard.css'
import './billing.css'
import { reportPageView } from './pageview.js'
import { buildLoginUrl } from './auth-state.js'
import { capabilityEnabled } from './rollout.js'
import { getLocale, onLocaleChange } from './locale.js'
import { formatMessage, getMessages } from './i18n.js'
import { formatDurationSeconds, formatStorage } from './formatters.js'
const statusEl = document.getElementById('billing-status')
const currentRoot = document.getElementById('current-billing')
const plansGrid = document.getElementById('billing-plans-grid')
const ordersBody = document.getElementById('billing-orders-body')
let billingContext = {
  phase: null,
  publicCapabilities: {},
  userCapabilities: {},
}
let currentBillingBody = null
let plansList = []
let ordersList = []

function t() {
  return getMessages('pages/billing', getLocale())
}

function setStatus(text, kind = 'info') {
  if (!statusEl) return
  const message = String(text || '').trim()
  statusEl.textContent = message
  statusEl.hidden = !message
  const normalizedKind = kind === true ? 'error' : kind === false ? 'info' : String(kind || 'info')
  statusEl.dataset.kind = normalizedKind
}

function formatDate(ts) {
  return ts ? new Date(ts * 1000).toLocaleString(getLocale() === 'en' ? 'en-US' : 'zh-CN', { hour12: false }) : '-'
}

function formatPrice(priceCents, currency) {
  return `${(priceCents / 100).toFixed(2)} ${currency}`
}

function formatCompactNumber(value) {
  return new Intl.NumberFormat(getLocale() === 'en' ? 'en-US' : 'zh-CN').format(Number(value || 0))
}

function renderCurrentBilling(billing) {
  const labels = t().currentBilling
  const container = currentRoot.closest('.billing-overview-current')
  if (container) {
    container.className = `billing-overview-current billing-overview-current--${billing.plan.code || 'free'}`
  }
  currentRoot.innerHTML = `
    <div class="kv-row"><span class="muted">${labels[0]}</span><strong>${billing.plan.name}</strong></div>
    <div class="kv-row"><span class="muted">${labels[1]}</span><strong>${billing.plan.code}</strong></div>
    <div class="kv-row"><span class="muted">${labels[2]}</span><strong>${billing.plan.billing_cycle}</strong></div>
    <div class="kv-row"><span class="muted">${labels[3]}</span><strong>${formatDate(billing.subscription.current_period_end)}</strong></div>
    <div class="kv-row"><span class="muted">${labels[4]}</span><strong>${formatDurationSeconds(billing.quota.workspace_active_seconds)} / ${formatDurationSeconds(billing.capabilities.max_daily_workspace_seconds)}</strong></div>
    <div class="kv-row"><span class="muted">${labels[5]}</span><strong>${formatStorage(billing.quota.storage_bytes)} / ${formatStorage(billing.capabilities.max_storage_bytes)}</strong></div>
  `
}

function compareRows(features) {
  const labels = t().compareRows
  return [
    { type: 'metric', label: labels.runs, value: formatDurationSeconds(features.max_daily_workspace_seconds || 0), icon: 'fas fa-chart-line' },
    { type: 'metric', label: labels.storage, value: formatStorage(features.max_storage_bytes || 0), icon: 'fas fa-database' },
    { type: 'boolean', label: labels.privateProjects, value: !!features.private_projects, icon: 'fas fa-lock' },
    { type: 'boolean', label: labels.exports, value: !!features.exports, icon: 'fas fa-download' },
    { type: 'boolean', label: labels.education, value: !!features.classroom_management, icon: 'fas fa-chalkboard-user' },
    { type: 'boolean', label: labels.teams, value: !!features.team_collaboration, icon: 'fas fa-users' },
  ]
}

function renderFeatureValue(row) {
  const text = t()
  if (row.type === 'metric') {
    return `<span class="billing-plan-feature-value"><span class="metric-highlight">${row.value}</span></span>`
  }
  const enabled = row.value === true
  const valueClass = enabled ? '' : ' is-no'
  return `
    <span class="billing-plan-feature-value${valueClass}">
      <span class="billing-plan-feature-sub">${enabled ? text.yes : text.no}</span>
    </span>
  `
}

function planBadges(plan, current) {
  const text = t()
  const badges = []
  if (plan.is_recommended) {
    badges.push(`<span class="billing-plan-badge billing-plan-badge--recommended"><i class="fas fa-fire"></i>${text.recommendedPill}</span>`)
  }
  return badges.join('')
}

async function createCheckout(planCode) {
  const res = await fetch('/api/billing/checkout', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ plan_code: planCode }),
  })
  const body = await res.json().catch(() => ({}))
  if (res.status === 401) {
    window.location.href = buildLoginUrl('/billing')
    return
  }
  if (!res.ok) throw new Error(body.detail || t().createOrderFailed)
  setStatus(formatMessage(t().orderCreated, { orderNo: body.order_no }))
  await loadBilling()
}

function renderPlans(plans, currentPlanCode) {
  plansGrid.innerHTML = ''
  const checkoutEnabled = capabilityEnabled(billingContext.userCapabilities, 'billing_checkout', false)
  const text = t()
  plans.forEach((plan) => {
    const current = plan.code === currentPlanCode
    const action = current
      ? `<button class="btn-plan outline" disabled style="opacity:0.6; cursor:not-allowed;"><i class="fas fa-check"></i>${text.currentPlanPill}</button>`
      : checkoutEnabled
        ? `<button class="inline-action primary btn-plan primary" data-plan-code="${plan.code}">${text.createOrder}<i class="fas fa-arrow-right"></i></button>`
        : `<button class="btn-plan outline" disabled style="opacity:0.6; cursor:not-allowed;">${text.unavailable}</button>`
    const card = document.createElement('article')
    card.className = `billing-plan-card billing-plan-card--${plan.theme || plan.code}${current ? ' is-current' : ''}${plan.is_recommended ? ' is-recommended' : ''}`
    if (current) card.dataset.currentLabel = `✓ ${text.currentPlanPill}`
    card.innerHTML = `
      <div class="billing-plan-badges">${planBadges(plan, current)}</div>
      <div class="billing-plan-header">
        <h3 class="billing-plan-name">${plan.name}</h3>
        <div class="billing-plan-price-row">
          <span class="billing-plan-price">${formatPrice(plan.price_cents, plan.currency)}</span>
          <span class="billing-plan-cycle">${text.cycles[plan.billing_cycle] || plan.billing_cycle}</span>
        </div>
        <p class="billing-plan-summary">${plan.description || ''}</p>
      </div>
      <ul class="billing-plan-features">
        ${compareRows(plan.features).map((row) => `
          <li class="billing-plan-feature">
            <span class="billing-plan-feature-icon ${row.type === 'boolean' ? (row.value ? 'is-yes' : 'is-no') : 'is-yes'}" aria-hidden="true">
              <i class="${row.icon}"></i>
            </span>
            <div class="billing-plan-feature-main">
              <div class="billing-plan-feature-label">${row.label}</div>
            </div>
            ${renderFeatureValue(row)}
          </li>
        `).join('')}
      </ul>
      <div class="billing-plan-actions">${action}</div>
    `
    plansGrid.appendChild(card)
  })
  document.querySelectorAll('[data-plan-code]').forEach((button) => {
    button.addEventListener('click', () => {
      createCheckout(button.dataset.planCode).catch((error) => setStatus(error.message || t().placeOrderFailed, true))
    })
  })
}

function renderOrders(orders) {
  ordersBody.innerHTML = ''
  if (!orders.length) {
    ordersBody.innerHTML = `<tr><td colspan="5" class="empty">${t().noOrders}</td></tr>`
    return
  }
  const statusLabels = t().orderStatuses || {}
  orders.forEach((order) => {
    const tr = document.createElement('tr')
    const paid = order.status === 'paid'
    const failed = order.status === 'failed'
    const statusIcon = paid ? 'fas fa-check-circle' : failed ? 'fas fa-circle-xmark' : 'fas fa-hourglass-half'
    const statusText = statusLabels[order.status] || order.status || '-'
    tr.innerHTML = `
      <td><span class="billing-order-id">${order.order_no}</span></td>
      <td>${order.plan_code || '-'}</td>
      <td>${formatPrice(order.amount_cents, order.currency)}</td>
      <td><span class="billing-status-badge ${paid ? 'is-paid' : ''}"><i class="${statusIcon}"></i>${statusText}</span></td>
      <td>${formatDate(order.created_at)}</td>
    `
    ordersBody.appendChild(tr)
  })
}

async function loadBilling() {
  setStatus(t().refreshing)
  const [billingRes, plansRes, ordersRes] = await Promise.all([
    fetch('/api/billing/me', { credentials: 'include' }),
    fetch('/api/billing/plans', { credentials: 'include' }),
    fetch('/api/billing/orders', { credentials: 'include' }),
  ])
  if (billingRes.status === 401) {
    window.location.href = buildLoginUrl('/billing')
    return
  }
  const billingBody = await billingRes.json().catch(() => ({}))
  const plansBodyJson = await plansRes.json().catch(() => ({}))
  const ordersBodyJson = await ordersRes.json().catch(() => ({}))
  if (!billingRes.ok) throw new Error(billingBody.detail || t().loadCurrentFailed)
  if (!plansRes.ok) throw new Error(plansBodyJson.detail || t().loadPlansFailed)
  if (!ordersRes.ok) throw new Error(ordersBodyJson.detail || t().loadOrdersFailed)
  currentBillingBody = billingBody
  plansList = plansBodyJson.plans || []
  ordersList = ordersBodyJson.orders || []
  billingContext = {
    phase: plansBodyJson.phase || null,
    publicCapabilities: plansBodyJson.public_capabilities || {},
    userCapabilities: billingBody.capabilities || {},
  }
  renderCurrentBilling(currentBillingBody)
  renderPlans(plansList, billingBody.plan.code)
  renderOrders(ordersList)
  if (!capabilityEnabled(billingBody.capabilities || {}, 'billing_checkout', false)) {
    setStatus(t().phaseClosed, 'warning')
  } else {
    setStatus(t().synced, 'success')
  }
}

function applyLocalizedText() {
  const text = t()
  document.title = text.title
  document.querySelector('.dashboard-brand-text')?.replaceChildren(document.createTextNode(text.brandName || '天元计算'))
  document.getElementById('billing-link-account').textContent = text.account
  document.getElementById('billing-link-workspace').textContent = text.workspace
  document.getElementById('billing-title-current').textContent = text.currentTitle
  document.getElementById('billing-title-plans').textContent = text.plansTitle
  document.getElementById('billing-copy-plans').textContent = text.plansCopy
  document.getElementById('billing-title-orders').textContent = text.ordersTitle
  document.getElementById('billing-th-order-no').textContent = text.columns.orderNo
  document.getElementById('billing-th-order-plan').textContent = text.columns.plan
  document.getElementById('billing-th-amount').textContent = text.columns.amount
  document.getElementById('billing-th-status').textContent = text.columns.status
  document.getElementById('billing-th-time').textContent = text.columns.time
  if (currentBillingBody) {
    renderCurrentBilling(currentBillingBody)
    renderPlans(plansList, currentBillingBody.plan.code)
    renderOrders(ordersList)
  }
}

applyLocalizedText()
onLocaleChange(applyLocalizedText)
reportPageView('/billing')
loadBilling().catch((error) => {
  setStatus(error.message || t().genericLoadFailed, true)
})
